import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext, useLocation } from 'react-router-dom';
import { useModalConfirm } from '../useModalConfirm';
import { ExclamationCircleOutlined } from '@ant-design/icons';

/**
 * Use this hook to block navigation based on a custom condition or action.
 * The blocker function defines the condition or action to evaluate.
 * @param {Function} blocker - Function that executes to determine blocking behavior.
 * @param {boolean} when - Condition to determine when blocking should be active.
 */
export function useBlocker(blocker, when = true) {
  const { navigator } = useContext(NavigationContext);
  const location = useLocation();

  useEffect(() => {
    if (!when) {
      return;
    }

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock(); // Unblock before retrying to avoid blocking loop
          tx.retry();
        }
      };

      // Prevent blocking if the target location is the same as the current
      if (autoUnblockingTx.location.pathname === location.pathname
          && autoUnblockingTx.location.search === location.search) {
        return;
      }

      blocker(autoUnblockingTx);
    });

    return unblock; // Cleanup function to remove the block when component unmounts or conditions change
  }, [navigator, blocker, when]);
}

/**
 * Hook to prompt the user with a modal confirmation dialog when navigating away.
 * @param {string} message - Message to display in the modal.
 * @param {boolean} when - Condition to activate the prompt.
 * @param {Function} onConfirm - Action to execute when the user confirms.
 * @param {object} rest - Additional props to pass to the Modal.
 */
export function usePrompt({ message, when = true, onConfirm = () => {}, ...rest }) {
  const { showModalConfirm } = useModalConfirm(when);  // Use `when` as the condition for showing the modal

  const blocker = useCallback((tx) => {
    showModalConfirm({
      content: message,
      icon: <ExclamationCircleOutlined />,
      defaultAction: () => tx.retry(),
      onConfirm: async () => {

        // If requested by the consumer
        // return the tx object for manual
        // handling of the action
        if (onConfirm.length > 0) {
          await onConfirm(tx);
        } else {
          await onConfirm();
          tx.retry();
        }
      },
      ...rest
    });
  }, [showModalConfirm, message]);

  return useBlocker(blocker, when);
}
